import * as R from 'ramda'
import store from '@/store'
import { formatSeconds } from '@/utils/utils'
import i18n from '@/locales'
import { getZoneTimeInfo, getTimeZoneName, getTimezoneOffset } from '@/utils/time'

/** 解析监控 interval 字符串（如 5m、1h），与监控页 timeGroup 一致 */
function parseMonitorInterval (val) {
  const s = String(val || '').trim()
  const m = s.match(/^(\d+)([smhd])$/i)
  if (!m) {
    return { num: 5, unit: 'm' }
  }
  return { num: Math.max(1, Number(m[1])), unit: m[2].toLowerCase() }
}

function formatMonitorIntervalLabel (val) {
  const { num, unit } = parseMonitorInterval(val)
  if (unit === 'm') return i18n.t('common_num_minutes', [num])
  if (unit === 'h') return i18n.t('common_num_hours', [num])
  return `${num}${unit}`
}

const MONITOR_INTERVAL_UNIT_OPTIONS = [
  { key: 'm', label: i18n.t('subDurations.minutes') },
  { key: 'h', label: i18n.t('subDurations.hours') },
]

export const GLOBAL_SETTING_OPTIONS_SORT = [
  'common$enable_quota_check',
  'compute$cloudaccount_health_status_check',
  'common$global_http_proxy',
  'identity$password_unique_history_check',
  'identity$password_minimal_length',
  'identity$password_error_lock_count',
  'identity$password_expiration_seconds',
  'identity$password_char_complexity',
  'identity$token_expiration_seconds',
  'identity$no_action_logout_seconds',
  'yunionapi$session_level_auth_cookie',
  'yunionapi$is_forget_login_user',
  'common$non_default_domain_projects',
  'common$api_server',
  'yunionapi$show_captcha',
  'yunionapi$enable_totp',
  'yunionapi$return_full_domain_list',
  'compute$enable_pending_delete',
  'compute$pending_delete_expire_seconds',
  'image$enable_pending_delete',
  'image$pending_delete_expire_seconds',
  'baremetal$auto_register_baremetal',
  'baremetal$default_ipmi_password',
  'yunionapi$force_single_session_login',
  'compute$enable_pre_allocate_ip_addr',
  'meter$exchange_rate_available',
  'meter$cost_conversion_available',
  'meter$cost_conversion_origin',
  'common$ignore_nonrunning_guests',
  'compute$enable_auto_switch_server_sku',
  'log$syslog_url',
  'yunionapi$enable_syslog_webservice',
  'yunionapi$syslog_webservice_username',
  'yunionapi$syslog_webservice_password',
  'compute$enable_tls_migration',
  'yunionapi$enable_organization',
  'common$create_server_auto_start',
  'common$server_delete_limit',
  'cloudmon$disable_ping_probe',
  'cloudmon$collect_metric_interval',
  'cloudmon$cloudpods_collect_delay_minutes',
  'compute$skip_server_by_sys_tag_keys',
  'compute$default_admin_security_group_id',
  'yunionapi$hide_web_error_details',
  'meter$enable_prediction',
  // 'meter$share_resource_type',
  'meter$use_local_instance_project',
  'compute$disable_sync_cloud_event',
  'compute$enable_export_username_password',
  'common$enable_cloud_shell',
  'webconsole$enable_watermark',
  'webconsole$enable_command_recording',
  'webconsole$referer_whitelist',
  'compute$local_sys_disk_min_size_gb',
  'compute$local_sys_disk_max_size_gb',
  'compute$local_data_disk_min_size_gb',
  'compute$local_data_disk_max_size_gb',
  'common$time_zone',
  'compute$enable_auto_rename_project',
  'identity$force_enable_mfa',
  'region$ssl_accounts',
  'monitor$resource_metrics_default_interval',
]

export const GLOBAL_SETTING_OPTIONS = {
  IDENTITY: {
    password_unique_history_check: {
      component: 'a-input-number',
      props: { type: 'number', min: 0 },
      belong: 'iam',
    },
    password_minimal_length: {
      component: 'a-input-number',
      props: { type: 'number', min: 1 },
      defaultValue: 6,
      belong: 'iam',
    },
    password_error_lock_count: {
      component: 'a-input-number',
      props: { type: 'number', min: 0 },
      belong: 'iam',
    },
    password_expiration_seconds: {
      component: 'duration-second-input',
      props: { min: 0 },
      tableFormatter: ({ cellValue }) => {
        return formatSeconds(cellValue).str || '0'
      },
      belong: 'iam',
    },
    token_expiration_seconds: {
      component: 'duration-second-input',
      props: { min: 0 },
      tableFormatter: ({ cellValue }) => {
        return formatSeconds(cellValue).str || '0'
      },
      belong: 'iam',
    },
    no_action_logout_seconds: {
      component: 'duration-second-input',
      props: {
        min: 0,
        // durationOptions: [
        //   { label: i18n.t('subDurations').minutes, key: 'minutes' },
        //   { label: i18n.t('subDurations').hours, key: 'hours' },
        //   { label: i18n.t('subDurations').days, key: 'days' },
        // ],
      },
      tableFormatter: ({ cellValue }) => {
        return formatSeconds(cellValue).str || '0'
      },
      ok: (key, val, serviceId) => {
        store.commit('auth/SET_NO_ACTION_LOGOUT_SECONDS', val)
      },
      belong: 'iam',
    },
    password_char_complexity: {
      component: 'a-select',
      options: [
        { key: 1, label: i18n.t('system.password_char_complexity_1') },
        { key: 2, label: i18n.t('system.password_char_complexity_2') },
        { key: 3, label: i18n.t('system.password_char_complexity_3') },
        { key: 4, label: i18n.t('system.password_char_complexity_4') },
      ],
      defaultValue: 1,
      formatter: (val) => val || 1,
      belong: 'iam',
    },
    force_enable_mfa: {
      component: 'a-select',
      options: [
        { key: 'disable', label: i18n.t('system.disable_mfa') },
        { key: 'after', label: i18n.t('system.enable_mfa_new_user') },
        { key: 'all', label: i18n.t('system.enable_mfa_all_user') },
      ],
      defaultValue: 'disable',
      formatter: (val) => {
        if (val === 'after') {
          return i18n.t('system.enable_mfa_new_user')
        } else if (val === 'all') {
          return i18n.t('system.enable_mfa_all_user')
        }
        return i18n.t('system.disable_mfa')
      },
      belong: 'iam',
    },
  },
  COMMON: {
    non_default_domain_projects: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185'), disabled: true },
      ],
      dialog: 'GlobalSettingL3PermissionEnableDialog',
      defaultValue: () => `${store.getters.l3PermissionEnable}`,
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'iam',
    },
    api_server: {
      component: 'a-input',
      dialog: 'GlobalSettingUpdateDialog',
      defaultValue: () => window.location.origin,
      parser: (val) => {
        let v = val
        if (v.endsWith('.')) {
          v = v.substring(0, v.length - 1)
        }
        if (v.endsWith('/')) {
          v = v.substring(0, v.length - 1)
        }
        return v
      },
      formatter: (val) => {
        let v = val
        if (v.endsWith('.')) {
          v = v.substring(0, v.length - 1)
        }
        if (v.endsWith('/')) {
          v = v.substring(0, v.length - 1)
        }
        return v
      },
      ok: (key, val, serviceId) => {
        // update store.auth.regions
        store.dispatch('auth/getRegions')
        const currentHost = window.location.hostname
        if (val) {
          if (!val.includes(currentHost)) {
            store.dispatch('common/updateObject', {
              name: 'topAlert',
              data: {
                apiServer: {
                  messageOptions: [
                    i18n.t('system.text_186'),
                    ['a', { attrs: { href: val } }, val],
                    i18n.t('system.text_187'),
                  ],
                  interval: 1000 * 60 * 60 * 24,
                },
              },
            })
          } else {
            store.dispatch('common/deleteObject', {
              name: 'topAlert',
              key: 'apiServer',
            })
          }
        }
      },
      belong: 'system',
    },
    enable_quota_check: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      defaultValue: () => 'false', // 默认关闭配额检查
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        store.dispatch('auth/getInfo')
        store.commit('common/SET_GLOBAL_CONFIG', { ...store.getters.globalConfig, enable_quota_check: val === 'true' })
      },
      belong: 'iam',
    },
    global_http_proxy: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      dialog: 'GlobalHttpProxyDialog',
      formatter: (val, opt, config) => {
        if (config && (config.global_http_proxy || config.global_https_proxy)) return 'true'
        if (val === 'true' || val === 'false') return val
        return 'false'
      },
      parser: (val) => val === 'true',
      defaultValue: () => 'false', // 默认关闭配额检查
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'system',
    },
    ignore_nonrunning_guests: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'compute',
    },
    create_server_auto_start: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => {
        const { create_server_auto_start = true } = store.getters.globalSetting.value || {}
        return create_server_auto_start ? 'true' : 'false'
      },
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      skipUpdate: true,
      ok: (key, val, serviceId) => {
        store.dispatch('globalSetting/putFetchGlobalSettingValue', { create_server_auto_start: val === 'true' })
      },
      belong: 'compute',
    },
    server_delete_limit: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => {
        const { server_delete_limit = false } = store.getters.globalSetting.value || {}
        return server_delete_limit ? 'true' : 'false'
      },
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      skipUpdate: true,
      ok: (key, val, serviceId) => {
        store.dispatch('globalSetting/putFetchGlobalSettingValue', { server_delete_limit: val === 'true' })
      },
      belong: 'compute',
    },
    enable_cloud_shell: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        store.commit('common/SET_GLOBAL_CONFIG', { ...store.getters.globalConfig, enable_cloud_shell: val === 'true' })
      },
      belong: 'system',
    },
    time_zone: {
      component: 'a-select',
      options: getZoneTimeInfo().map(item => {
        return { ...item, label: `(${item.offsetText}) ${item.label}` }
      }),
      props: {
        showSearch: true,
        filterOption: (input, option) => {
          const text = option.componentOptions?.children[0]?.text || ''
          return text.includes(input)
        },
      },
      formatter: (val) => {
        if (!val) return ''
        const offset = getTimezoneOffset(val)
        return `(${offset.offsetText}) ${getTimeZoneName(val)}`
      },
      belong: 'system',
    },
  },
  YUNIONAPI: {
    show_captcha: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'iam',
    },
    enable_totp: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'iam',
    },
    session_level_auth_cookie: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'iam',
    },
    return_full_domain_list: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_185') },
        { key: 'false', label: i18n.t('system.text_184') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_185')
        return i18n.t('system.text_184')
      },
      belong: 'iam',
    },
    enable_syslog_webservice: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'system',
    },
    syslog_webservice_username: {
      component: 'a-input',
      belong: 'system',
    },
    syslog_webservice_password: {
      component: 'a-input-password',
      belong: 'system',
    },
    force_single_session_login: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      defaultValue: () => 'false', // 默认允许多 session 登录
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'iam',
    },
    is_forget_login_user: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      defaultValue: () => 'false',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        store.commit('auth/SET_REGIONS', { ...store.state.auth.regions, is_forget_login_user: val })
      },
      belong: 'iam',
    },
    enable_organization: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, value, serviceId) => {
        store.commit('projectTags/SET_DATA', { name: 'enableOrganization', data: value === 'true' })
      },
      belong: 'iam',
    },
    hide_web_error_details: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val) => {
        store.commit('auth/SET_REGIONS', { ...store.state.auth.regions, hide_web_error_details: val === 'true' })
      },
      belong: 'system',
    },
  },
  NOTIFY: {
    // verify_email_url_path: {
    //   component: 'a-input',
    //   tableFormatter: ({ cellValue }) => {
    //     return cellValue || '-'
    //   },
    // },
  },
  COMPUTE: {
    enable_pending_delete: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        store.commit('common/SET_COMPUTEV2_GLOBAL_CONFIG', { ...store.getters.computeV2GlobalConfig, enable_pending_delete: val === 'true' })
      },
      belong: 'compute',
    },
    cloudaccount_health_status_check: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'cloudenv',
    },
    pending_delete_expire_seconds: {
      component: 'duration-second-input',
      tableFormatter: ({ cellValue }) => {
        return formatSeconds(cellValue).str
      },
      belong: 'compute',
    },
    enable_pre_allocate_ip_addr: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'compute',
    },
    enable_auto_switch_server_sku: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'compute',
    },
    skip_server_by_sys_tag_keys: {
      component: 'a-input',
      belong: 'cloudenv',
      label: i18n.t('common_112'),
    },
    disable_sync_cloud_event: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_185') },
        { key: 'false', label: i18n.t('system.text_184') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_185')
        return i18n.t('system.text_184')
      },
      belong: 'cloudenv',
    },
    default_admin_security_group_id: {
      component: 'a-input',
      belong: 'compute',
    },
    enable_tls_migration: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'compute',
    },
    enable_export_username_password: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'compute',
    },
    local_sys_disk_min_size_gb: {
      component: 'a-input-number',
      props: { type: 'number', min: 10 },
      defaultValue: 10,
      belong: 'compute',
    },
    local_sys_disk_max_size_gb: {
      component: 'a-input-number',
      props: { type: 'number', min: 2048 },
      defaultValue: 2048,
      belong: 'compute',
    },
    local_data_disk_min_size_gb: {
      component: 'a-input-number',
      props: { type: 'number', min: 10 },
      defaultValue: 10,
      belong: 'compute',
    },
    local_data_disk_max_size_gb: {
      component: 'a-input-number',
      props: { type: 'number', min: 40960 },
      defaultValue: 40960,
      belong: 'compute',
    },
    enable_auto_rename_project: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      defaultValue: () => 'false',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'cloudenv',
    },
    ssl_accounts: {
      component: 'a-input',
      belong: 'system',
      formatter: (val) => {
        return val && R.is(String, val) ? val.split(',').map(item => item.trim()) : (R.is(Array, val) ? val : [])
      },
      // parser: (val) => val.split(',').map(item => item.replace(/"/g, '')),
    },
  },
  IMAGE: {
    enable_pending_delete: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        store.commit('common/SET_IMAGE_GLOBAL_CONFIG', { ...store.getters.imageGlobalConfig, enable_pending_delete: val === 'true' })
      },
      belong: 'compute',
    },
    pending_delete_expire_seconds: {
      component: 'duration-second-input',
      tableFormatter: ({ cellValue }) => {
        return formatSeconds(cellValue).str
      },
      belong: 'compute',
    },
  },
  BAREMETAL: {
    auto_register_baremetal: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'compute',
    },
    default_ipmi_password: {
      component: 'a-input-password',
      belong: 'compute',
    },
  },
  METER: {
    exchange_rate_available: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      ok: (key, val, serviceId) => {
        store.dispatch('SET_BILL_EXCHANGE_RATE_AVAILABLE', val)
        location.reload()
      },
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'bill',
    },
    cost_conversion_available: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      defaultValue: () => 'false', // 默认允许多 session 登录
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        location.reload()
      },
      belong: 'bill',
    },
    cost_conversion_origin: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      defaultValue: () => 'false', // 默认允许多 session 登录
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        store.dispatch('SET_BILL_COST_CONVERSION_ORIGIN', val)
        location.reload()
      },
      belong: 'bill',
    },
    enable_prediction: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      ok: (key, val, serviceId) => {
        store.commit('SET_GLOBAL_BILL_CONFIG', { ...store.state.common.bill.globalConfig, enable_prediction: val })
        location.reload()
      },
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'bill',
    },
    // share_resource_type: {
    //   component: 'a-select',
    //   options: [
    //     { key: 'resource_type', label: i18n.t('system.resource_type') },
    //     { key: 'origin_res_type', label: i18n.t('system.origin_res_type') },
    //   ],
    //   defaultValue: () => 'origin_res_type',
    //   ok: (key, val, serviceId) => {
    //     store.commit('SET_GLOBAL_BILL_CONFIG', { ...store.state.common.bill.globalConfig, share_resource_type: val })
    //     location.reload()
    //   },
    //   formatter: (val) => `${val}`,
    //   tableFormatter: ({ cellValue }) => {
    //     const v = cellValue
    //     if (v === 'resource_type') return i18n.t('system.resource_type')
    //     return i18n.t('system.origin_res_type')
    //   },
    //   belong: 'bill',
    // },
    use_local_instance_project: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'bill',
    },
  },
  LOG: {
    syslog_url: {
      component: 'a-input',
      belong: 'system',
    },
  },
  CLOUDMON: {
    disable_ping_probe: {
      component: 'a-select',
      options: [
        { key: 'false', label: i18n.t('system.text_184') },
        { key: 'true', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'false'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'compute',
    },
    collect_metric_interval: {
      component: 'a-input-number',
      props: { type: 'number', min: 1 },
      defaultValue: 6,
      formatter: (val) => Number(val),
      parser: (val) => Number(val),
      tableFormatter: ({ cellValue }) => i18n.t('common_num_minutes', [Number(cellValue) || 0]),
      belong: 'compute',
    },
    cloudpods_collect_delay_minutes: {
      component: 'a-input-number',
      props: { type: 'number', min: 0 },
      defaultValue: 0,
      formatter: (val) => Number(val),
      parser: (val) => Number(val),
      tableFormatter: ({ cellValue }) => i18n.t('common_num_minutes', [Number(cellValue) || 0]),
      belong: 'compute',
    },
  },
  WEBCONSOLE: {
    enable_watermark: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'true',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      ok: (key, val, serviceId) => {
        store.commit('common/SET_GLOBAL_CONFIG', { ...store.getters.globalConfig, enable_watermark: val === 'true' })
      },
      belong: 'system',
    },
    enable_command_recording: {
      component: 'a-select',
      options: [
        { key: 'true', label: i18n.t('system.text_184') },
        { key: 'false', label: i18n.t('system.text_185') },
      ],
      defaultValue: () => 'false',
      formatter: (val) => `${val}`,
      parser: (val) => val === 'true',
      tableFormatter: ({ cellValue }) => {
        let v = cellValue
        if (R.is(String, v)) {
          v = v === 'true'
        }
        if (v) return i18n.t('system.text_184')
        return i18n.t('system.text_185')
      },
      belong: 'system',
    },
    referer_whitelist: {
      component: 'a-input',
      belong: 'iam',
      notRequired: true,
    },
  },
  // KEYSTONE: {
  //   force_enable_mfa: {
  //     component: 'a-select',
  //     options: [
  //       { key: 'disable', label: i18n.t('system.disable_mfa') },
  //       { key: 'after', label: i18n.t('system.enable_mfa_new_user') },
  //       { key: 'all', label: i18n.t('system.enable_mfa_all_user') },
  //     ],
  //     defaultValue: 'disable',
  //     formatter: (val) => {
  //       if (val === 'after') {
  //         return i18n.t('system.enable_mfa_new_user')
  //       } else if (val === 'all') {
  //         return i18n.t('system.enable_mfa_all_user')
  //       }
  //       return i18n.t('system.disable_mfa')
  //     },
  //     belong: 'iam',
  //   },
  // },
  MONITOR: {
    resource_metrics_default_interval: {
      component: 'monitor-interval-input',
      defaultValue: '5m',
      props: {
        min: 1,
        unitOptions: MONITOR_INTERVAL_UNIT_OPTIONS,
      },
      formatter: (val) => String(val || '5m').trim(),
      parser: (val) => {
        const { num, unit } = parseMonitorInterval(val)
        return `${num}${unit}`
      },
      tableFormatter: ({ cellValue }) => formatMonitorIntervalLabel(cellValue),
      belong: 'compute',
    },
  },
}
